/**
 * useAnimationControls - Creates standard animation control callbacks.
 * Extracted from AnimationContext and useAnimationInstance to eliminate duplication.
 */

import React, { useCallback } from 'react'
import type { AnimationAction, PlaybackSpeed } from '../types/Animation.types'

export type AnimationControls = {
	play: () => void
	pause: () => void
	stop: () => void
	reset: () => void
	toggle: () => void
	seek: (progress: number) => void
	setSpeed: (speed: PlaybackSpeed) => void
	toggleLoop: () => void
	toggleGhostTrail: () => void
}

/**
 * Hook that creates stable callback references for animation controls.
 * All callbacks are memoized and only depend on the dispatch function.
 * This prevents unnecessary re-renders when passing control methods as props.
 *
 * @param dispatch - Dispatch function for animation actions
 * @param isPlaying - Current playing state (needed for toggle)
 * @returns Object containing all control methods
 *
 * @example
 * // In AnimationContext - provides context-wide controls
 * const { play, pause, stop, seek, setSpeed, reset, toggleGhostTrail, toggleLoop } =
 *   useAnimationControls(dispatch, state.isPlaying)
 *
 * @example
 * // In useAnimationInstance - provides instance-specific controls
 * const controls = useAnimationControls(dispatch, state.isPlaying)
 * return {
 *   state,
 *   ...controls, // Spread all control methods
 *   isReady,
 *   isPlaying,
 *   isComplete,
 *   progress,
 * }
 *
 * @example
 * // Using individual controls in a component
 * const { play, pause, toggle } = useAnimationControls(dispatch, isPlaying)
 * return (
 *   <button onClick={toggle}>
 *     {isPlaying ? <Pause /> : <Play />}
 *   </button>
 * )
 */
export function useAnimationControls(
	dispatch: React.Dispatch<AnimationAction>,
	isPlaying: boolean,
): AnimationControls {
	const play = useCallback(() => {
		dispatch({ type: 'PLAY' })
	}, [dispatch])

	const pause = useCallback(() => {
		dispatch({ type: 'PAUSE' })
	}, [dispatch])

	const stop = useCallback(() => {
		dispatch({ type: 'STOP' })
	}, [dispatch])

	const reset = useCallback(() => {
		dispatch({ type: 'RESET' })
	}, [dispatch])

	const toggle = useCallback(() => {
		if (isPlaying) {
			dispatch({ type: 'PAUSE' })
		} else {
			dispatch({ type: 'PLAY' })
		}
	}, [isPlaying, dispatch])

	const seek = useCallback((progress: number) => {
		dispatch({ type: 'SEEK', progress })
	}, [dispatch])

	const setSpeed = useCallback((speed: PlaybackSpeed) => {
		dispatch({ type: 'SET_SPEED', speed })
	}, [dispatch])

	const toggleLoop = useCallback(() => {
		dispatch({ type: 'TOGGLE_LOOP' })
	}, [dispatch])

	const toggleGhostTrail = useCallback(() => {
		dispatch({ type: 'TOGGLE_GHOST_TRAIL' })
	}, [dispatch])

	return {
		play,
		pause,
		stop,
		reset,
		toggle,
		seek,
		setSpeed,
		toggleLoop,
		toggleGhostTrail,
	}
}
